import React, { useEffect, useState } from 'react';
import { Image, ImageProps } from '@chakra-ui/react';
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

export async function getSignedS3Url(s3Client: S3Client | null, src: string): Promise<string> {
    if (!src) return '';
    if (src.startsWith('blob:') || src.startsWith('data:') || !s3Client) {
        return src;
    }
    if (src.includes('X-Amz-Signature') || src.includes('AWSAccessKeyId')) {
        return src;
    }
    try {
        let bucket = '';
        let key = '';

        // Pattern 1: https://{bucket}.s3.{region}.amazonaws.com/{key} or https://{bucket}.s3-{region}.amazonaws.com/{key} or https://{bucket}.s3.amazonaws.com/{key}
        const match1 = src.match(/^https:\/\/([^.]+)\.s3[-.][^/]+\.amazonaws\.com\/(.+)$/i) ||
                       src.match(/^https:\/\/([^.]+)\.s3\.amazonaws\.com\/(.+)$/i);

        // Pattern 2: https://s3.{region}.amazonaws.com/{bucket}/{key} or https://s3.amazonaws.com/{bucket}/{key}
        const match2 = src.match(/^https:\/\/s3[-.][^/]+\.amazonaws\.com\/([^/]+)\/(.+)$/i) ||
                       src.match(/^https:\/\/s3\.amazonaws\.com\/([^/]+)\/(.+)$/i);

        if (match1) {
            bucket = match1[1];
            key = decodeURIComponent(match1[2].split('?')[0]);
        } else if (match2) {
            bucket = match2[1];
            key = decodeURIComponent(match2[2].split('?')[0]);
        } else if (!src.startsWith('http://') && !src.startsWith('https://')) {
            bucket = import.meta.env.VITE_EDIT_BUCKET_NAME || '';
            key = src;
        }

        if (bucket && key) {
            const command = new GetObjectCommand({
                Bucket: bucket,
                Key: key
            });
            return await getSignedUrl(s3Client as any, command as any, { expiresIn: 3600 });
        }
    } catch (e) {
        console.error("Failed to sign S3 URL in getSignedS3Url:", e);
    }
    return src;
}

interface SignedImageProps extends ImageProps {
    s3Client: S3Client | null;
    src: string;
    onSignedUrlReady?: (url: string) => void;
}

export const SignedImage: React.FC<SignedImageProps> = ({ s3Client, src, onSignedUrlReady, ...props }) => {
    const [signedUrl, setSignedUrl] = useState<string>('');

    useEffect(() => {
        let isMounted = true;
        const loadSignedUrl = async () => {
            if (!src) return;
            const signed = await getSignedS3Url(s3Client, src);
            if (isMounted) {
                setSignedUrl(signed);
                if (onSignedUrlReady) onSignedUrlReady(signed);
            }
        };

        loadSignedUrl();
        return () => {
            isMounted = false;
        };
    }, [src, s3Client]);

    return <Image src={signedUrl || src} {...props} />;
};

export default SignedImage;

